import { RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { decideCollapsedCount, getCollapsedSteps } from 'utils/stepBarMode';

interface UseStepBarModeArgs {
  stepCount: number;
  /** Index of the current step in display order (-1 if none). It always keeps its label. */
  currentIndex: number;
  /** Changes whenever something that can change a step's width changes (steps shown, completion, required dots). */
  layoutKey: string;
}

interface UseStepBarModeResult {
  /** Per step, in display order: true when the step shows as an icon only. */
  collapsed: boolean[];
  /** Attach to a zero-height box that is exactly as wide as the space the bar may use. */
  widthRef: RefObject<HTMLDivElement>;
  /** Attach to an invisible `width: max-content` copy of the bar with every step labelled. */
  measureFullRef: RefObject<HTMLDivElement>;
  /** Attach to an invisible `width: max-content` copy of the bar with every step as an icon. */
  measureCompactRef: RefObject<HTMLDivElement>;
}

const itemWidths = (copy: HTMLElement): number[] => Array.from(copy.querySelectorAll('li.ant-menu-item')).map((item) => item.getBoundingClientRect().width);

/**
 * Decides, by measurement rather than breakpoints, which steps of the Create/Edit step bar show as icons only.
 *
 * Two invisible copies of the bar give every step's labelled and icon-only width. The decision (`decideCollapsedCount`)
 * is made in a layout effect, before the browser paints, whenever the current step or a step's content changes, and by
 * a ResizeObserver when the space or a copy changes size (window resize, Defective Returns appearing, fonts loading).
 * The observer re-renders synchronously, so a frame with the old layout at the new width is never painted.
 *
 * Neither observed box changes size when steps collapse or the bar wraps (the width box has no height, the copies
 * never collapse), so a decision cannot feed back into the next one.
 */
export const useStepBarMode = ({ stepCount, currentIndex, layoutKey }: UseStepBarModeArgs): UseStepBarModeResult => {
  const widthRef = useRef<HTMLDivElement>(null);
  const measureFullRef = useRef<HTMLDivElement>(null);
  const measureCompactRef = useRef<HTMLDivElement>(null);
  const [collapsedCount, setCollapsedCount] = useState(0);

  const measure = useCallback((): void => {
    const space = widthRef.current;
    const fullCopy = measureFullRef.current;
    const compactCopy = measureCompactRef.current;

    if (!space || !fullCopy || !compactCopy) return;

    const fullWidths = itemWidths(fullCopy);
    const compactWidths = itemWidths(compactCopy);

    if (fullWidths.length !== stepCount || compactWidths.length !== stepCount) return;

    // Anything the menu adds around its items (padding, border), read from the labelled copy.
    const extraWidth = Math.max(0, fullCopy.getBoundingClientRect().width - fullWidths.reduce((sum, width) => sum + width, 0));
    const items = fullWidths.map((fullWidth, index) => ({ fullWidth, compactWidth: compactWidths[index], isCurrent: index === currentIndex }));
    const containerWidth = space.getBoundingClientRect().width;

    // The functional update returns the same number when nothing changed, so React bails out of re-rendering.
    setCollapsedCount((now) => decideCollapsedCount({ containerWidth, items, collapsedCountNow: now, extraWidth }));
  }, [stepCount, currentIndex]);

  useLayoutEffect(() => {
    measure();
  }, [measure, layoutKey]);

  useLayoutEffect(() => {
    const onResize = (): void => {
      flushSync(() => {
        measure();
      });
    };

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', onResize);

      return (): void => window.removeEventListener('resize', onResize);
    }

    const observer = new ResizeObserver(onResize);

    [widthRef.current, measureFullRef.current, measureCompactRef.current].forEach((element) => {
      if (element) observer.observe(element);
    });

    return (): void => observer.disconnect();
  }, [measure]);

  const steps = Array.from({ length: stepCount }, (_, index) => ({ isCurrent: index === currentIndex }));

  return { collapsed: getCollapsedSteps(steps, collapsedCount), widthRef, measureFullRef, measureCompactRef };
};
